// Shared comping vocabulary. Swing develops phrases in compPhrases.ts;
// Latin retains repeating figures in the planner below. Both are deterministic
// and react to the written score; neither listens to live audio or MIDI.
// The vocabulary's historical source annotations are retained. The current
// implementation choices and independently checked references are documented
// in docs/interactive-comping-review.md.

import { PHI, spread } from './walkingBass';
import { passRole, planSwingCompPhrases } from './compPhrases';
import { planFunkCompRhythm } from './compFunk';
import type { PassKind } from './performance';

/** A melody note the planner listens to (absolute in-form beats). */
export interface CompRhythmNote {
  beat: number;
  durationBeats: number;
}

export interface CompRhythmChord {
  startBeat: number;
  durationBeats: number;
}

export interface CompRhythmOptions {
  /** Beat where the first full bar begins (anacrusis offset). */
  barOffset?: number;
  /** Activity 0–1: the density knob (the mixer's comp amount). */
  amount?: number;
  /** Arrangement tempo — picks the ballad / medium / fast vocabulary. */
  bpm?: number;
  /** The written melody, absolute beats — what the planner comps AROUND. */
  melody?: CompRhythmNote[];
  /** Chorus boundaries (absolute beats) for the intensity arc. */
  passStarts?: number[];
  /** What each pass is under an itinerary (head / solo / head-out), aligned
   * with `passStarts`; absent, the arc reads position (see `passRole`). */
  passKinds?: readonly PassKind[];
  /** The band's time-feel (a latin bass ⇒ 'latin'); default swing. */
  feel?: CompFeel;
  /** The meter: 3 selects the jazz-waltz vocabulary; anything else is 4. */
  beatsPerBar?: number;
}

/** One planned attack, before chord binding (which comping.ts owns). */
export interface PlannedAttack {
  beat: number;
  durationBeats: number;
  velocity: number;
  /** Short, accented upbeat; dynamics still follow the phrase level. */
  accent?: boolean;
  /** Per-note attack spread in seconds. Ordinary swing chords stay tight. */
  rollSec?: number;
}

type Band = 0 | 1 | 2; // sparse | medium | dense
type TempoBand = 'ballad' | 'medium' | 'fast';

/**
 * The comp's time-feel, decided by the BAND (a bossa's tumbao bass), never a
 * user knob here. Latin flips the engine into McNeely's percussive role
 * (Karita): "the rhythm section does not play like a Latin-American group…
 * we do, however, occasionally quote Latin rhythms", and the quoting is
 * "definite, repetitive figures" with "the same voicing used for each note
 * of the figure" — so the planner RIDES one figure instead of developing
 * motifs, and the realization neither accents nor shortens the tied
 * anticipations (a bossa push rings; a bebop kick stabs).
 */
export type CompFeel = 'swing' | 'latin' | 'funk';

export interface RhythmCell {
  id: string;
  bars: 1 | 2;
  /** Attack onsets in beats from the cell start, on the eighth grid. */
  attacks: number[];
  /** Optional written durations, including ties across beats/barlines.
   * Generic cells can instead let the phrase planner choose articulation. */
  durations?: number[];
  /** Density bands the cell serves (0 sparse, 1 medium, 2 dense). */
  bands: Band[];
  /** Tempo bands where the cell reads. */
  tempi: TempoBand[];
  /** Feels the cell serves; absent = swing only. */
  feels?: CompFeel[];
  /** Meters the cell serves; absent = 4/4 only. Waltz cells author their
   * attacks in 3-beat bars (a 2-bar cell spans 6 beats). */
  meters?: (3 | 4)[];
  /** A superimposed cross rhythm — "entered mid-form and EXITED deliberately,
   * a temporary excitement device" (Patton 5C). The planner never chains one:
   * ridden wall-to-wall it stops being a device and erases the meter, which
   * is what a whole chorus of the dotted-quarter cycle sounded like. */
  temporary?: boolean;
  /** Where the figure comes from — provenance, pinned by test. */
  source: string;
}

/**
 * The vocabulary. Every cell above the sparsest band carries at least one
 * upbeat attack (Crook: consistent downbeats inhibit forward motion) —
 * pinned by test, so a new cell can't quietly deaden the engine.
 */
export const RHYTHM_CELLS: RhythmCell[] = [
  // --- breathing & pads (pacing; ballads live here) ---
  { id: 'breath', bars: 1, attacks: [], bands: [0, 1], tempi: ['ballad', 'medium', 'fast'],
    feels: ['swing', 'latin'], meters: [3, 4],
    source: 'Crook III.3: rhythm sentences separated by rest measures' },
  { id: 'pad', bars: 1, attacks: [0], bands: [0], tempi: ['ballad', 'medium'],
    feels: ['swing', 'latin'], meters: [3, 4],
    source: 'McNeely, Round Midnight: "lots of long, sustained sounds"' },
  { id: 'pad-and2', bars: 1, attacks: [1.5], bands: [0, 1], tempi: ['ballad', 'medium'],
    meters: [3, 4],
    source: 'Crook III.2: sustained upbeat attack = forward motion' },
  { id: 'and4-over', bars: 2, attacks: [3.5], bands: [0, 1], tempi: ['ballad', 'medium'],
    source: 'Crook III.4: the upbeat tied over the bar line — the anticipation archetype' },
  { id: 'rm-pair', bars: 1, attacks: [2, 2.5], bands: [1], tempi: ['ballad'],
    source: 'McNeely, Round Midnight: double-time feel = shorter, detached figures' },

  // --- the supportive medium-swing core ---
  { id: 'charleston', bars: 1, attacks: [0, 1.5], bands: [1], tempi: ['medium'],
    source: 'the Charleston — canonical' },
  { id: 'reverse-charleston', bars: 1, attacks: [1.5, 3], bands: [1], tempi: ['medium'],
    source: 'the displaced Charleston — canonical' },
  { id: 'and1-3', bars: 1, attacks: [0.5, 2], bands: [1], tempi: ['medium'],
    source: 'Crook III.9 motif family: upbeat start, downbeat answer' },
  { id: '2-and3', bars: 1, attacks: [1, 2.5], bands: [1], tempi: ['medium'],
    source: 'Crook III.9 motif family' },
  { id: 'and2-and4', bars: 1, attacks: [1.5, 3.5], bands: [1, 2], tempi: ['medium'],
    source: 'Crook III.6: phrases ending on the upbeat; the and-of-4 anticipates' },
  { id: '1-and4', bars: 1, attacks: [0, 3.5], bands: [1], tempi: ['medium'],
    source: 'Crook III.9 motif family: downbeat pad, pushed exit' },
  { id: 'energize', bars: 2, attacks: [0, 6, 7.5], bands: [1], tempi: ['medium'],
    source: 'McNeely, Karita: two-bar phrase, "energize the second half of the second bar"' },
  { id: 'held-pickup-answer', bars: 2, attacks: [0, 3.5, 4.5, 7.5],
    durations: [3, 0.5, 3, 0.5], bands: [0, 1], tempi: ['ballad', 'medium'],
    source: 'Sustained/pickup/answer rhythm; Levine, Aebersold 60, Little Sunflower opening (rhythm only)' },

  // --- dense (solo climaxes) ---
  { id: '1-and2-4', bars: 1, attacks: [0, 1.5, 3], bands: [2], tempi: ['medium'],
    source: 'Crook III.6: balanced short/sustained mixes' },
  { id: 'and1-and2-4', bars: 1, attacks: [0.5, 1.5, 3], bands: [2], tempi: ['medium'],
    source: 'Crook III.9 motif family' },
  { id: '1-25-and4', bars: 1, attacks: [0, 2.5, 3.5], bands: [2], tempi: ['medium'],
    source: 'Crook III.9 motif family' },
  { id: 'dotted-quarter-cross', bars: 2, attacks: [0, 1.5, 3, 4.5, 6, 7.5], bands: [2], tempi: ['medium'],
    temporary: true,
    source: 'Patton 5C: the dotted-quarter cross rhythm — a temporary superimposition' },
  { id: 'burst-and-release', bars: 2, attacks: [0, 1.5, 2, 3, 3.5, 6],
    durations: [1.5, 0.5, 1, 0.5, 1.5, 1], bands: [2], tempi: ['medium'],
    temporary: true,
    source: 'Derived dense answer followed by space; Haerle/Levine transcription review' },

  // --- latin: quoted figures, ridden repetitively (McNeely, Karita) ---
  { id: 'bossa-clave', bars: 2, attacks: [0, 1.5, 4.5, 6], bands: [0, 1],
    tempi: ['ballad', 'medium', 'fast'], feels: ['latin'],
    source: 'the bossa nova comping pattern, 3-2 (the clave convention at the eighth level)' },
  { id: 'bossa-clave-23', bars: 2, attacks: [1, 2.5, 4, 5.5], bands: [1],
    tempi: ['ballad', 'medium', 'fast'], feels: ['latin'],
    source: 'the bossa nova comping pattern, 2-3 side' },
  { id: 'karita-onbeat', bars: 2, attacks: [0, 2, 4, 6, 6.5], bands: [1, 2],
    tempi: ['ballad', 'medium', 'fast'], feels: ['latin'],
    source: 'McNeely, Karita: first chorus on the beat in two-bar phrases, ' +
      '"energizing" the second bar\'s back half with two eighths' },
  { id: 'samba-offbeat', bars: 2, attacks: [0, 1.5, 3.5, 5, 6.5], bands: [2],
    tempi: ['ballad', 'medium', 'fast'], feels: ['latin'],
    source: 'McNeely, Karita: second chorus larger and more off the beat, ' +
      'opening with a typical samba rhythm (derived)' },

  // --- 3/4: the jazz-waltz vocabulary (attacks in 3-beat bars) ---
  { id: 'w-2', bars: 1, attacks: [1], bands: [0, 1], tempi: ['ballad', 'medium', 'fast'],
    meters: [3],
    source: 'the jazz waltz: beat 2 alone — the lilt, with the downbeat left to the bass' },
  { id: 'w-23', bars: 1, attacks: [1, 2], bands: [1], tempi: ['ballad', 'medium', 'fast'],
    meters: [3],
    source: 'the classic jazz-waltz comp: beats 2 and 3' },
  { id: 'w-1-and2', bars: 1, attacks: [0, 1.5], bands: [1], tempi: ['ballad', 'medium', 'fast'],
    meters: [3],
    source: 'the Charleston displaced into three: 1 and the and of 2' },
  { id: 'w-and2-over', bars: 2, attacks: [1.5], bands: [0, 1], tempi: ['ballad', 'medium', 'fast'],
    meters: [3],
    source: 'Crook III.4 in three: the tied upbeat over the bar line' },
  { id: 'w-2-and3', bars: 1, attacks: [1, 2.5], bands: [1, 2], tempi: ['ballad', 'medium', 'fast'],
    meters: [3],
    source: 'beat 2 answered on the and of 3 — the pushed exit into the next bar' },
  { id: 'w-duple', bars: 2, attacks: [0, 2, 4], bands: [1, 2], tempi: ['ballad', 'medium', 'fast'],
    meters: [3],
    source: 'Patton 5C: Wynton Kelly comping in twos across 3/4 (Someday My Prince Will Come)' },
  { id: 'w-hemiola', bars: 2, attacks: [0, 1.5, 3, 4.5], bands: [2], tempi: ['ballad', 'medium', 'fast'],
    meters: [3], temporary: true,
    source: 'the dotted-quarter hemiola — 3/4’s own cross rhythm, a temporary superimposition' },

  // --- fast: the half-note pulse (syncopation moves up a level) ---
  { id: 'fast-1', bars: 1, attacks: [0], bands: [0, 1], tempi: ['fast'],
    source: 'McNeely, Last Minute: feel a half-note pulse' },
  { id: 'fast-1-3', bars: 1, attacks: [0, 2], bands: [1, 2], tempi: ['fast'],
    source: 'McNeely, Last Minute: the half-note pulse stated' },
  { id: 'fast-and2-over', bars: 2, attacks: [1.5], bands: [0, 1], tempi: ['fast'],
    source: 'McNeely, Last Minute: sustained values tied across the bar line' },
  { id: 'fast-3-and4', bars: 1, attacks: [2, 3.5], bands: [2], tempi: ['fast'],
    source: 'Crook III.2 at the half-note level: the upbeat exit' },
];

/**
 * Crook's forward-motion reading of a cell: the share of its attacks that
 * are upbeats, plus a bonus when the cell EXITS on an upbeat ("phrases
 * ending often on the upbeat"). Exported for the tests, and used by the
 * planner to keep all-downbeat cells from chaining.
 */
export function forwardMotion(cell: RhythmCell): number {
  if (!cell.attacks.length) return 0;
  const upbeats = cell.attacks.filter((a) => a % 1 !== 0).length;
  const endsUp = cell.attacks[cell.attacks.length - 1] % 1 !== 0 ? 0.5 : 0;
  return upbeats / cell.attacks.length + endsUp;
}

const tempoBandOf = (bpm: number): TempoBand => (bpm <= 88 ? 'ballad' : bpm >= 224 ? 'fast' : 'medium');

/** Deterministic 0..1 fraction per bar (golden-ratio hash, like pickFor). */
const frac = (bar: number, salt: number): number => ((((bar + 1) * PHI + salt) % 1) + 1) % 1;

interface MelodyProfile {
  /** Note onsets, sorted. */
  onsets: number[];
  /** Onsets that begin a phrase (first sound after ≥ 2 beats of silence). */
  phraseStarts: Set<number>;
  sounding(beat: number): boolean;
  onsetsIn(from: number, to: number): number;
}

function profileMelody(melody: CompRhythmNote[] | undefined): MelodyProfile | null {
  if (!melody || melody.length === 0) return null;
  const notes = [...melody].sort((a, b) => a.beat - b.beat);
  const onsets = notes.map((n) => n.beat);
  const phraseStarts = new Set<number>();
  let coveredTo = -Infinity;
  for (const n of notes) {
    if (n.beat - coveredTo >= 2 - 1e-6) phraseStarts.add(n.beat);
    coveredTo = Math.max(coveredTo, n.beat + n.durationBeats);
  }
  return {
    onsets,
    phraseStarts,
    sounding: (beat) => notes.some((n) => n.beat <= beat + 1e-6 && beat < n.beat + n.durationBeats - 1e-6),
    onsetsIn: (from, to) => onsets.filter((o) => o >= from - 1e-6 && o < to - 1e-6).length,
  };
}

/**
 * Plan the attacks for a form. Returns beats/durations/velocities only —
 * binding each attack to a chord (including the half-beat anticipation rule)
 * stays in comping.ts, which owns that vocabulary.
 */
export function planCompRhythm(
  chords: CompRhythmChord[],
  opts: CompRhythmOptions = {},
): PlannedAttack[] {
  if (chords.length === 0) return [];
  if (opts.feel === 'funk' && (opts.beatsPerBar ?? 4) === 4) {
    return planFunkCompRhythm(chords, opts);
  }
  // Swing develops phrases with explicit rests and articulation. Keep the
  // repeating Latin figures on their own planner; they serve another role.
  if (opts.feel !== 'latin' || opts.beatsPerBar === 3) {
    return planSwingCompPhrases(chords, opts, RHYTHM_CELLS);
  }
  // The vocabulary speaks 4/4 and 3/4; other meters fall back upstream.
  const beatsPerBar = opts.beatsPerBar === 3 ? 3 : 4;
  const barOffset = opts.barOffset ?? 0;
  const amount = opts.amount ?? 0.5;
  const tempo = tempoBandOf(opts.bpm ?? 140);
  // A feel whose vocabulary has no real figures for this meter (latin has no
  // 3/4 cells yet) comps in swing instead — pads-only would read as broken,
  // and a jazz-waltz treatment of a latin 3/4 tune is the honest fallback.
  const wantFeel = opts.feel ?? 'swing';
  const feel: CompFeel = RHYTHM_CELLS.some(
    (cell) =>
      (cell.feels ?? ['swing']).includes(wantFeel) &&
      (cell.meters ?? [4]).includes(opts.beatsPerBar === 3 ? 3 : 4) &&
      cell.attacks.length > 1,
  )
    ? wantFeel
    : 'swing';
  const melody = profileMelody(opts.melody);
  const totalBeats = Math.max(...chords.map((c) => c.startBeat + c.durationBeats));
  const changeBeats = chords.map((c) => c.startBeat).filter((b) => b > barOffset + 1e-6);
  const passStarts = opts.passStarts && opts.passStarts.length > 1 ? opts.passStarts : undefined;

  // The density knob maps to a base band; each pass then shifts it: the head
  // (pass 0) stays at base, middle passes climb one band, and the final pass
  // of three or more comes back down to hand off (McNeely: the voicings come
  // down at the end of the solo to pave the way).
  const baseBand: Band = amount < 0.34 ? 0 : amount < 0.72 ? 1 : 2;
  const passIndexAt = (beat: number): number => {
    if (!passStarts) return 0;
    let pass = 0;
    for (let i = 0; i < passStarts.length; i++) if (beat >= passStarts[i] - 1e-6) pass = i;
    return pass;
  };
  const roleAt = (beat: number) =>
    passRole(passIndexAt(beat), passStarts?.length ?? 1, opts.passKinds);
  const bandAt = (beat: number): Band => {
    if (!passStarts) return baseBand;
    return Math.min(2, baseBand + (roleAt(beat) === 'solo' ? 1 : 0)) as Band;
  };
  // The DYNAMIC arc mirrors the density arc: the head sits under the solo
  // choruses, and the out-head comes down to hand off (McNeely) — density
  // was climbing by pass already, but every attack at one loudness is what
  // reads as mechanical.
  const passLevelAt = (beat: number): number => {
    if (!passStarts) return 1;
    const role = roleAt(beat);
    return role === 'head' ? 0.88 : role === 'out' ? 0.85 : 1;
  };

  // --- choose a cell per slot ------------------------------------------------
  const chosen: { barStart: number; cell: RhythmCell }[] = [];
  let previous: RhythmCell | null = null;
  let barsSinceBreath = 0;

  for (let barStart = barOffset; barStart < totalBeats - 1e-6; ) {
    const barIndex = Math.round((barStart - barOffset) / beatsPerBar);
    const band = bandAt(barStart);
    const poolFor = (wantFeel: CompFeel) =>
      RHYTHM_CELLS.filter((cell) => {
        if (!(cell.feels ?? ['swing']).includes(wantFeel)) return false;
        if (!(cell.meters ?? [4]).includes(beatsPerBar as 3 | 4)) return false;
        if (!cell.tempi.includes(tempo)) return false;
        // A band's cells plus its neighbours' — the score prefers the exact band.
        if (!cell.bands.some((b) => Math.abs(b - band) <= 1)) return false;
        if (cell.bars === 2 && barStart + 2 * beatsPerBar > totalBeats + 1e-6) return false;
        if (cell.id === 'breath') {
          if (barIndex === 0) return false; // the form opens with sound
          if (previous?.attacks.length === 0) return false; // never two silent bars
        }
        return true;
      });
    // A feel whose vocabulary doesn't cover this meter (latin has no 3/4
    // cells yet) answers with the swing pool rather than crashing or going
    // silent — a comp that exists beats an idiom that doesn't.
    let candidates = poolFor(feel);
    if (!candidates.length) candidates = poolFor('swing');

    let best: RhythmCell = candidates[0];
    let bestScore = -Infinity;
    for (const cell of candidates) {
      let score = 0;
      // Exact band beats a neighbour.
      score += cell.bands.includes(band) ? 1.5 : 0;
      // Each band has a real activity target (attacks per bar) — without
      // this the forward-motion share lets single-upbeat cells crowd out
      // the two-attack figures a supportive medium swing actually plays.
      // Scaled by the meter: a 3-beat bar holds proportionally fewer attacks.
      const target = (0.75 + band) * (beatsPerBar / 4);
      score += 1.0 - Math.min(1.2, Math.abs(cell.attacks.length / cell.bars - target) * 0.8);
      // Forward motion, and no chaining of all-downbeat cells (Crook III.2).
      const fm = forwardMotion(cell);
      score += fm;
      if (fm === 0 && cell.attacks.length > 0 && previous && forwardMotion(previous) === 0) score -= 1.5;
      // Motif development, shaped like SENTENCES (the fix for "unmusical",
      // Aug 2026: with every bar re-decided the comp changed its mind
      // constantly — no ridden figure, no phrase shape). Inside a 4-bar
      // phrase the previous figure is mildly favoured, so a motif RIDES its
      // sentence; at a phrase boundary the old sometimes-repeat rule stands,
      // so a new sentence may state a new idea. Latin rides flat-out
      // (McNeely's percussive role: "definite, repetitive figures"), and a
      // TEMPORARY superimposition never chains — a cross rhythm ridden
      // wall-to-wall erases the meter, which is exactly what a whole chorus
      // of the dotted-quarter cycle sounded like.
      const phrasePos = barIndex % 4;
      if (previous && cell.id === previous.id) {
        if (cell.temporary) score -= 3;
        else if (feel === 'latin') score += 1.5;
        else if (phrasePos !== 0) score += 1.1;
        else score += spread(barIndex, 0.35, 0.61) ? 1.2 : -1.0;
      }
      // Pacing: after six busy bars a breath is welcome, and a phrase's last
      // bar is where a sentence naturally punctuates (Crook's "rhythm
      // sentences separated by rest measures").
      if (cell.id === 'breath' && barsSinceBreath >= 6) score += 2;
      if (cell.id === 'breath' && phrasePos === 3) score += 1.0;
      // The opening bar should state the top of the form.
      if (barIndex === 0 && cell.attacks.some((a) => a < 1e-6)) score += 1.5;

      // Harmonic changes inside the cell's span, each counted ONCE: a change
      // the cell states (an attack within half a beat either side — Crook's
      // anticipation or the beat itself) helps, and a change left unsounded
      // for a whole beat is the old harmony smearing into the new one, which
      // no comper plays. Per-change, never per-attack: the old per-attack
      // bonus paid dense cells for having more attacks, not better ones.
      const spanEnd = barStart + cell.bars * beatsPerBar;
      for (const change of changeBeats) {
        if (change < barStart - 1e-6 || change >= spanEnd - 1e-6) continue;
        const stated = cell.attacks.some((a) => Math.abs(barStart + a - change) <= 0.5 + 1e-6);
        if (stated) score += 0.8;
        else if (
          !cell.attacks.some((a) => {
            const b = barStart + a;
            return b >= change - 0.5 - 1e-6 && b <= change + 1 + 1e-6;
          })
        ) {
          score -= 1.2;
        }
      }

      if (melody && cell.attacks.length) {
        // Melody interaction is the MEAN over the cell's attacks, not the
        // sum — summing made the hole-filling bonus scale with attack count,
        // so the densest cell won every hole and Patton's cross rhythm
        // became the permanent texture. Averaged, the terms judge WHERE the
        // attacks sit and the band target owns HOW MANY there are.
        let interaction = 0;
        for (const a of cell.attacks) {
          const b = barStart + a;
          const busy = melody.onsetsIn(b, b + 1);
          const atOnset = melody.onsets.some((o) => Math.abs(o - b) < 0.25);
          if (busy >= 2) interaction -= 2.5; // don't chatter under a run
          else if (atOnset) {
            // Reinforce a phrase's entrance; don't shadow every note.
            const entrance = melody.onsets.some(
              (o) => Math.abs(o - b) < 0.25 && melody.phraseStarts.has(o),
            );
            interaction += entrance ? 1.0 : -0.75;
          } else if (!melody.sounding(b)) interaction += 1.5; // fill the hole
          else interaction += 0.5; // a pad under a held note is classic
        }
        score += (interaction / cell.attacks.length) * 1.6;
      }
      // Deterministic tie-break so equal cells vary across the form.
      score += frac(barIndex, 0.29 + RHYTHM_CELLS.indexOf(cell) * 0.013) * 0.5;
      if (score > bestScore) {
        bestScore = score;
        best = cell;
      }
    }

    chosen.push({ barStart, cell: best });
    previous = best;
    barsSinceBreath = best.id === 'breath' ? 0 : barsSinceBreath + best.bars;
    barStart += best.bars * beatsPerBar;
  }

  // --- realize durations, dynamics & touch --------------------------------
  //
  // The aliveness layer (Aug 2026, "it's very rigid — no dynamics"). Three
  // deterministic devices, each from the sources:
  //
  //   PHRASE DYNAMICS. Every attack at one loudness is what reads as
  //   mechanical. Crook's practice list names dynamics ("exaggerating
  //   extremes") and McNeely tracks intensity per section, so each 4-bar
  //   phrase sits at its own level (golden-ratio, 0.72–1.0 — soft answers
  //   and fuller statements), scaled again by the pass arc: the head and
  //   the out-head sit under the solo choruses.
  //
  //   SHORT/SUSTAINED VARIETY (Crook III:8). His motif notation writes every
  //   cell BOTH ways — short values and sustained values — and invites
  //   "combinations of short and sustained note values from the above rhythm
  //   motif". Ringing every attack to the next is only one such combination,
  //   so some mid-figure attacks detach into dry quarter-ish hits.
  //
  //   THE CLICHÉ SWEEP. A long pad occasionally realizes as a slow upward
  //   roll (McNeely's "rolled chords" technique) — tempo-aware, so the
  //   sweep reads as a gesture, not a glitch, at any bpm.
  //
  // Latin keeps its even, percussive surface: no accents, no detached
  // variety, no sweeps — repetition IS that idiom — but the slow phrase
  // levels still breathe underneath it.
  const spb = 60 / (opts.bpm ?? 140);
  const attacks = chosen.flatMap(({ barStart, cell }) =>
    cell.attacks.map((a) => barStart + a),
  );
  const clampVel = (v: number) => Math.min(1, Math.max(0.45, v));
  const out: PlannedAttack[] = attacks.map((beat, i) => {
    const next = i + 1 < attacks.length ? attacks[i + 1] : totalBeats + 1;
    const gap = next - beat;
    const upbeat = beat % 1 !== 0;
    const phrase = Math.floor((beat - barOffset) / (beatsPerBar * 4));
    const level = (0.72 + 0.28 * frac(phrase, 0.83)) * passLevelAt(beat);

    // An upbeat followed by space is, per Crook, EITHER the accented short
    // attack OR sustained over the beat/bar line — his forward-motion rule
    // names both. Realizing every one as a stab was itself mechanical, so
    // the choice is deterministic per attack: mostly the kick, sometimes the
    // tied-over pad. NOT in latin: a bossa's anticipation always RINGS.
    if (gap >= 1.5 - 1e-6 && upbeat && feel !== 'latin' && spread(i, 0.55, 0.71)) {
      return {
        beat,
        durationBeats: 0.45,
        velocity: clampVel(1.08 * level),
        accent: true,
        rollSec: 0.005,
      };
    }

    const role = upbeat ? 0.82 : 0.92;
    let durationBeats = Math.max(0.4, gap - 0.12);
    let velocity = clampVel(role * level);
    let rollSec = gap >= 1.5 ? 0.012 : 0.006;
    if (feel !== 'latin') {
      if (gap >= 1 - 1e-6 && gap < 3 && spread(i, 0.3, 0.43)) {
        // Crook III:8 — the short realization of the same attack point.
        durationBeats = Math.min(durationBeats, 0.4);
        rollSec = 0.005;
      } else if (gap >= 3 - 1e-6 && spread(i, 0.3, 0.16)) {
        // NB the salt: two spread() gates on the SAME index are correlated
        // by their salt offset — the accent gate (0.55 @ 0.71) admits
        // frac ∈ [0.29, 0.84), and a sweep window inside that range would
        // never see an attack the accent branch hadn't already taken. 0.16
        // puts the sweep window in the accent gate's complement.
        // The cliché upward sweep on a long pad — a downbeat pad or a tied
        // anticipation alike: ~0.6 beat across the hand, a shade under the
        // phrase level.
        rollSec = Math.min(0.12, 0.15 * spb);
        velocity = clampVel(role * level * 0.92);
      }
    }
    return { beat, durationBeats, velocity, rollSec };
  });
  return out;
}
